// Plan catalog — served by the backend (GET /api/v1/plans), which resolves it
// from the STRIPE_PRICE_* env vars sitting next to STRIPE_SECRET.
//
// Nothing about Stripe is hardcoded here on purpose. Price ids differ between
// Stripe test and live mode, so a baked-in id silently breaks the moment the
// API's key points at the other mode ("No such price: price_..."). Letting the
// server own the ids keeps the key and the prices in the same mode by
// construction.
//
// Response shape:
//   [{ key, name, membership_type,
//      options: [{ interval, price_id, amount, currency, hidden }] }]
// `hidden` options are configured and subscribable but not promoted — keep them
// out of the picker while still allowing an existing subscriber to resubscribe
// to the plan they were on.
// The API omits intervals whose price env var is unset, so an empty array means
// "nothing is configured" — surface that rather than guessing a fallback.

import { useEffect, useMemo, useState } from 'react';
import axiosInstance from './axiosConfig';
import { authHeaders, parseApiError } from './billingApi';

// Plan preselected on the publish / custom-domain upsell.
export const DEFAULT_PLAN_KEY = 'standard';
export const DEFAULT_INTERVAL = 'monthly';

// The API treats "annual" as an alias for "yearly". Normalize both sides so
// subscription rows written under either spelling still match a catalog option.
export const normalizeInterval = (interval) => {
  if (!interval) return null;
  const value = String(interval).trim().toLowerCase();
  return value === 'annual' ? 'yearly' : value;
};

// The catalog does not change within a session, so share one request across
// every component that asks for it.
let pending = null;

export const fetchPlans = () => {
  if (!pending) {
    pending = axiosInstance
      .get('plans/', authHeaders())
      .then(({ data }) => (Array.isArray(data) ? data : []))
      .catch((requestError) => {
        pending = null; // a failed fetch must not poison later attempts
        throw requestError;
      });
  }
  return pending;
};

export const clearPlansCache = () => {
  pending = null;
};

// Flatten the catalog to one selectable row per plan + interval.
export const planChoices = (plans) =>
  (plans || []).flatMap((plan) =>
    (plan.options || []).map((option) => ({
      id: `${plan.key}:${normalizeInterval(option.interval)}`,
      planKey: plan.key,
      planName: plan.name,
      interval: normalizeInterval(option.interval),
      amount: option.amount,
      currency: option.currency,
      hidden: Boolean(option.hidden),
    })),
  );

// The options a customer may pick from: everything except hidden ones, plus
// `keepId` so a preselected hidden plan stays visible in its own picker.
export const visibleChoices = (choices, keepId = null) =>
  (choices || []).filter((choice) => !choice.hidden || choice.id === keepId);

// Find one plan + interval among flattened choices, or null when the catalog
// no longer offers it (a retired plan, or an interval whose price is unset).
export const findChoice = (choices, planKey, interval) => {
  const key = planKey ? String(planKey).trim().toLowerCase() : '';
  const wanted = normalizeInterval(interval);
  return (
    (choices || []).find(
      (choice) => choice.planKey === key && (!wanted || choice.interval === wanted),
    ) || null
  );
};

// Fetch-once hook. `choices` is empty until the catalog loads; pass
// enabled=false to skip the request until the UI actually needs it.
//
// `loaded` (not `loading`) is what tells an empty `choices` apart from a
// not-yet-started fetch. The effect can only set loading=true *after* the
// enabling render, so a caller keying off `loading` alone flashes its empty
// state for one frame every time it switches on.
export const usePlanChoices = (enabled = true) => {
  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) return undefined;
    let isMounted = true;
    setError('');
    fetchPlans()
      .then((data) => {
        if (isMounted) setPlans(data);
      })
      .catch((requestError) => {
        if (isMounted) setError(parseApiError(requestError, 'Could not load plans.'));
      })
      .finally(() => {
        if (isMounted) setLoaded(true);
      });
    return () => {
      isMounted = false;
    };
  }, [enabled]);

  const choices = useMemo(() => planChoices(plans), [plans]);

  return { choices, loaded, loading: Boolean(enabled) && !loaded && !error, error };
};
